/**
 * Test Bench for Virtual Labs with Unknowns
 *
 * This module provides functionality to:
 * - Load virtual lab configurations
 * - Discover unknown variables in lab data
 * - Generate UI for configuring unknowns
 * - Load labs with unknowns applied
 */

'use strict';

const TestBench = (function() {

    // State
    let currentLab = null;
    let labData = null;
    let unknowns = {};
    let unknownSpecs = {}; // Stores metadata about each unknown (type, min, max, etc.)
    let appModel = null;
    let appView = null;

    // UI Elements
    let labSelect, scanBtn, scanStatus, unknownsConfig, unknownsList;
    let randomizeBtn, loadLabBtn, vlabContainer, currentUnknownsDisplay;
    let errorDisplay;

    /**
     * Initialize the test bench
     */
    function init() {
        // Get UI elements
        labSelect = document.getElementById('lab-select');
        scanBtn = document.getElementById('scan-btn');
        scanStatus = document.getElementById('scan-status');
        unknownsConfig = document.getElementById('unknowns-config');
        unknownsList = document.getElementById('unknowns-list');
        randomizeBtn = document.getElementById('randomize-btn');
        loadLabBtn = document.getElementById('load-lab-btn');
        vlabContainer = document.getElementById('vlab-container');
        currentUnknownsDisplay = document.getElementById('current-unknowns');
        errorDisplay = document.getElementById('error-display');

        // Bind events
        labSelect.addEventListener('change', onLabSelected);
        scanBtn.addEventListener('click', onScanClicked);
        randomizeBtn.addEventListener('click', onRandomizeClicked);
        loadLabBtn.addEventListener('click', onLoadLabClicked);

        console.log('Test Bench initialized');
    }

    /**
     * Handle lab selection
     */
    function onLabSelected() {
        const labPath = labSelect.value;
        if (labPath) {
            currentLab = labPath;
            scanBtn.disabled = false;
            scanStatus.textContent = 'Click "Scan for Unknowns" to analyze this lab';
            hideError();
            unknownsConfig.style.display = 'none';
            vlabContainer.style.display = 'none';
        } else {
            currentLab = null;
            scanBtn.disabled = true;
            scanStatus.textContent = '';
        }
    }

    /**
     * Scan the selected lab for unknowns
     */
    async function onScanClicked() {
        if (!currentLab) return;

        scanStatus.textContent = 'Scanning lab for unknowns...';
        scanBtn.disabled = true;
        hideError();

        try {
            // Load all 6 JSON files
            labData = await loadLabData(currentLab);

            // Scan for unknowns
            const foundUnknowns = scanForUnknowns(labData);

            if (Object.keys(foundUnknowns).length === 0) {
                scanStatus.textContent = 'No unknowns found in this lab';
                unknownsConfig.style.display = 'none';
                showError('This lab does not appear to use unknown variables. Try selecting a different lab like "Metals".');
            } else {
                unknownSpecs = foundUnknowns;
                scanStatus.textContent = `Found ${Object.keys(foundUnknowns).length} unknown variable(s)`;

                // Initialize unknowns with default/random values
                initializeUnknowns();

                // Generate UI
                generateUnknownsUI();

                // Show the unknowns config section
                unknownsConfig.style.display = 'block';
            }
        } catch (error) {
            console.error('Error scanning lab:', error);
            scanStatus.textContent = 'Error scanning lab';
            showError(`Failed to load lab data: ${error.message}`);
        }

        scanBtn.disabled = false;
    }

    /**
     * Load all lab data files
     */
    async function loadLabData(labPath) {
        const basePath = `app/scripts/resources/assignments/${labPath}`;
        const files = [
            'configuration.json',
            'assignment.json',
            'species.json',
            'reactions.json',
            'spectra.json',
            'solutions.json'
        ];

        const data = {};

        for (const file of files) {
            const filename = file.replace('.json', '');
            try {
                const response = await fetch(`${basePath}/${file}`);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status} for ${file}`);
                }
                data[filename] = await response.json();
            } catch (error) {
                console.warn(`Could not load ${file}:`, error);
                // Some labs might not have all files, use empty object
                data[filename] = {};
            }
        }

        return data;
    }

    /**
     * Scan lab data for unknown placeholders
     * Returns object with unknown specs: {u1: {type: 'double', min: 3, max: 5, ...}, ...}
     */
    function scanForUnknowns(data) {
        const unknownsFound = {};

        function scanValue(value) {
            if (typeof value === 'string') {
                // Check for unknown placeholder patterns
                // Format 1: "u1,double,3,5,u" or "u1,integer,10,20,u"
                // Format 2: "u1" (simple reference)
                const complexPattern = /^(u\d+),(\w+),([\d.]+),([\d.]+),u$/;
                const simplePattern = /^u\d+$/;

                const complexMatch = value.match(complexPattern);
                if (complexMatch) {
                    const [, varName, type, min, max] = complexMatch;
                    unknownsFound[varName] = {
                        type: type,
                        min: parseFloat(min),
                        max: parseFloat(max)
                    };
                } else if (simplePattern.test(value)) {
                    // Simple reference without constraints
                    if (!unknownsFound[value]) {
                        unknownsFound[value] = {
                            type: 'double',
                            min: 0,
                            max: 100
                        };
                    }
                }
            } else if (Array.isArray(value)) {
                value.forEach(scanValue);
            } else if (value && typeof value === 'object') {
                Object.values(value).forEach(scanValue);
            }
        }

        // Scan all lab data
        Object.values(data).forEach(scanValue);

        return unknownsFound;
    }

    /**
     * Initialize unknowns with random values within their constraints
     */
    function initializeUnknowns() {
        unknowns = {};
        for (const [varName, spec] of Object.entries(unknownSpecs)) {
            unknowns[varName] = randomizeValue(spec);
        }
    }

    /**
     * Generate a random value based on spec
     */
    function randomizeValue(spec) {
        const range = spec.max - spec.min;
        const value = spec.min + Math.random() * range;

        if (spec.type === 'integer') {
            return Math.floor(value);
        } else {
            // Round to reasonable precision for doubles
            return Math.round(value * 1000000) / 1000000;
        }
    }

    /**
     * Generate UI for configuring unknowns
     */
    function generateUnknownsUI() {
        unknownsList.innerHTML = '';

        for (const [varName, spec] of Object.entries(unknownSpecs)) {
            const item = document.createElement('div');
            item.className = 'unknown-item';

            item.innerHTML = `
                <label>
                    ${varName}
                    <span class="unknown-meta">(${spec.type}, range: ${spec.min} - ${spec.max})</span>
                </label>
                <input
                    type="number"
                    id="input-${varName}"
                    value="${unknowns[varName]}"
                    min="${spec.min}"
                    max="${spec.max}"
                    step="${spec.type === 'integer' ? '1' : 'any'}"
                    data-varname="${varName}"
                />
            `;

            unknownsList.appendChild(item);

            // Add change listener
            const input = item.querySelector('input');
            input.addEventListener('change', (e) => {
                const value = parseFloat(e.target.value);
                const varName = e.target.dataset.varname;
                const spec = unknownSpecs[varName];

                // Validate range
                if (value < spec.min || value > spec.max) {
                    showError(`${varName} must be between ${spec.min} and ${spec.max}`);
                    e.target.value = unknowns[varName];
                } else {
                    unknowns[varName] = value;
                    hideError();
                }
            });
        }
    }

    /**
     * Randomize all unknown values
     */
    function onRandomizeClicked() {
        initializeUnknowns();

        // Update input fields
        for (const [varName, value] of Object.entries(unknowns)) {
            const input = document.getElementById(`input-${varName}`);
            if (input) {
                input.value = value;
            }
        }

        console.log('Randomized unknowns:', unknowns);
    }

    /**
     * Load the lab with unknowns applied
     */
    function onLoadLabClicked() {
        if (!labData || Object.keys(unknowns).length === 0) {
            showError('No unknowns configured. Please scan a lab first.');
            return;
        }

        try {
            // Read current values from inputs
            for (const varName of Object.keys(unknowns)) {
                const input = document.getElementById(`input-${varName}`);
                if (input) {
                    unknowns[varName] = parseFloat(input.value);
                }
            }

            // Create unknownString in the format expected by UnknownClientSide
            const unknownString = Object.entries(unknowns)
                .map(([key, value]) => `${key},${value}`)
                .join(',');

            // Set up window.thisProblem for UnknownClientSide to read
            window.thisProblem = {
                unknownString: unknownString,
                unknowns: unknowns
            };

            console.log('Loading lab with unknowns:', unknowns);
            console.log('Unknown string:', unknownString);

            // Apply unknowns to lab data using replaceUnknowns
            // We need to import the replaceUnknowns function
            // For now, we'll implement a simplified version here
            const processedData = applyUnknowns(labData, unknowns);

            // Display current unknowns
            displayCurrentUnknowns();

            // Show vlab container
            vlabContainer.style.display = 'block';

            // Initialize VLab
            if (appView) {
                // Clean up previous instance
                const vlabDiv = document.getElementById('vlab');
                vlabDiv.innerHTML = '';
            }

            appModel = new VLab.AppModel();
            appView = new VLab.AppView({
                model: appModel,
                el: document.getElementById('vlab'),
                vlab: processedData,
                domain: 'https://chemcollective.org/chem/jsvlab/'
            });

            console.log('VLab loaded successfully');
            hideError();

        } catch (error) {
            console.error('Error loading lab:', error);
            showError(`Failed to load lab: ${error.message}`);
        }
    }

    /**
     * Apply unknowns to lab data (simplified replaceUnknowns implementation)
     */
    function applyUnknowns(data, unknowns) {
        function replaceInValue(value) {
            if (typeof value === 'string') {
                // Check if this is an unknown reference
                for (const [unknownVar, unknownValue] of Object.entries(unknowns)) {
                    // Simple reference: "u1"
                    if (value === unknownVar) {
                        return unknownValue;
                    }
                    // Complex reference: "u1,double,3,5,u"
                    if (value.split(',')[0] === unknownVar) {
                        return unknownValue;
                    }
                }
                return value;
            } else if (Array.isArray(value)) {
                return value.map(replaceInValue);
            } else if (value && typeof value === 'object') {
                const result = {};
                for (const [key, val] of Object.entries(value)) {
                    result[key] = replaceInValue(val);
                }
                return result;
            }
            return value;
        }

        // Deep copy and replace
        const processed = {};
        for (const [key, value] of Object.entries(data)) {
            processed[key] = replaceInValue(value);
        }
        return processed;
    }

    /**
     * Display current unknowns being used
     */
    function displayCurrentUnknowns() {
        const items = Object.entries(unknowns)
            .map(([key, value]) => `<strong>${key}</strong> = ${value}`)
            .join(', ');

        currentUnknownsDisplay.innerHTML = `
            <div class="info-box">
                <strong>Current Unknowns:</strong> ${items}
            </div>
        `;
    }

    /**
     * Show error message
     */
    function showError(message) {
        errorDisplay.textContent = message;
        errorDisplay.style.display = 'block';
    }

    /**
     * Hide error message
     */
    function hideError() {
        errorDisplay.style.display = 'none';
    }

    // Public API
    return {
        init: init
    };
})();

// Auto-initialize when page loads
if (typeof window !== 'undefined') {
    window.TestBench = TestBench;
    window.addEventListener('DOMContentLoaded', () => {
        TestBench.init();
    });
}

export default TestBench;
